

import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

function Home() {
  // Animation Variants
  const fadeUp = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
  };

  const fadeIn = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 1.2, ease: "easeInOut" } },
  };

  const float = {
    y: [0, -15, 0],
    transition: {
      duration: 6,
      repeat: Infinity,
      ease: "easeInOut",
    },
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.6 }}
      className="home-container"
      style={{ fontFamily: "'Poppins', sans-serif" }}
    >
      {/* 🌸 Hero Section */}
      <motion.section
        className="hero-section d-flex flex-column align-items-center justify-content-center text-center"
        style={{
          minHeight: "100vh",
          background:
            "linear-gradient(160deg, #fcae48 0%, #ffd6b5 40%, #fff6f4 100%)",
          color: "#fff",
          padding: "50px 20px",
          position: "relative",
          overflow: "hidden",
        }}
        initial="hidden"
        animate="visible"
        variants={fadeIn}
      >
        {/* 🌤 Soft floating background circles */}
        <motion.div
          style={{
            position: "absolute",
            top: "-100px",
            right: "-80px",
            width: "300px",
            height: "300px",
            background: "radial-gradient(circle, rgba(255,255,255,0.3), transparent)",
            borderRadius: "50%",
            zIndex: 0,
          }}
          animate={float}
        />
        <motion.div
          style={{
            position: "absolute",
            bottom: "-120px",
            left: "-100px",
            width: "350px",
            height: "350px",
            background: "radial-gradient(circle, rgba(255,255,255,0.25), transparent)",
            borderRadius: "50%",
            zIndex: 0,
          }}
          animate={float}
        />

        {/* 🪔 Maharaj Ji Image */}
        <motion.img
          src="/pranamsagarji_icon.jpg"
          alt="Dr. Pranamsagar Ji Maharaj"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          style={{
            width: "300px",
            height: "300px",
            borderRadius: "60%",
            objectFit: "cover",
            boxShadow: "0 15px 35px rgba(0,0,0,0.25)",
            marginBottom: "25px",
            zIndex: 1,
            border: "4px solid rgba(255,255,255,0.5)",
            // marginTop: "-190px",
          }}
        />

        {/* ✨ Title */}
        <motion.h1
          className="display-4 fw-bold"
          style={{
            color: "#4b2e05",
            textShadow: "2px 3px 12px rgba(255, 235, 200, 0.6)",
            letterSpacing: "1px",
            zIndex: 1,
          }}
          variants={fadeUp}
        >
          Bhaktamarwale Baba
        </motion.h1>

        <motion.p
          style={{
            fontSize: "1.3rem",
            marginTop: "15px",
            color: "#5a3a0e",
            zIndex: 1,
          }}
          variants={fadeUp}
        >
          Guided by the teachings of{" "}
          <span style={{ fontWeight: "700", color: "#7c3b00" }}>
            Acharya Dr. Pranamsagarji Maharaj
          </span>
        </motion.p>

        {/* 🌼 Button Group */}
        <motion.div
          className="mt-4 d-flex gap-3 flex-wrap justify-content-center"
          variants={fadeUp}
        >
          {[
            { to: "/verses", label: "Explore Shlokas", color: "#ff5e62" },
            { to: "/healing", label: "Healing Power", color: "#ff9966" },
          ].map((btn, i) => (
            <Link
              key={i}
              to={btn.to}
              className="btn"
              style={{
                backgroundColor: "#fff",
                color: btn.color,
                padding: "12px 30px",
                borderRadius: "50px",
                fontWeight: "600",
                transition: "all 0.3s ease",
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = btn.color;
                e.currentTarget.style.color = "#fff";
                e.currentTarget.style.transform = "translateY(-3px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#fff";
                e.currentTarget.style.color = btn.color;
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              {btn.label}
            </Link>
          ))}

          {/* 📜 Bhaktamar Button */}
          <motion.div animate={float}>
            <Link
              to="/shlokas"
              className="btn bhaktamar-btn"
              style={{
                background: "linear-gradient(145deg, #b30000, #ff3333)",
                color: "#fff",
                padding: "12px 35px",
                borderRadius: "50px",
                fontWeight: "600",
                transition: "all 0.3s ease",
                boxShadow: "0 0 25px 5px rgba(255, 60, 60, 0.5)",
                animation: "blinkGlow 3s infinite ease-in-out",
              }}
            >
              भक्तामर स्तोत्र
            </Link>
          </motion.div>

          {/* 📘 PDF Button */}
          <a
            href="/assets/Bhaktamar_Book.pdf"
            download
            className="btn"
            style={{
              background: "linear-gradient(145deg, #8B0000, #cc0000)",
              color: "#fff",
              padding: "12px 35px",
              borderRadius: "50px",
              fontWeight: "600",
              boxShadow: "0 0 15px 3px rgba(255,0,0,0.4)",
              transition: "all 0.3s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "linear-gradient(145deg, #cc0000, #ff5555)";
              e.currentTarget.style.transform = "translateY(-3px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "linear-gradient(145deg, #8B0000, #cc0000)";
              e.currentTarget.style.transform = "translateY(0)";
            }}
          >
            भक्तामर PDF
          </a>
        </motion.div>

        {/* ✨ Blinking Glow Animation */}
        <style>
          {`
            @keyframes blinkGlow {
              0%, 100% {
                box-shadow: 0 0 12px 3px rgba(255, 200, 150, 0.4),
                            0 0 24px 6px rgba(255, 80, 80, 0.5);
              }
              50% {
                box-shadow: 0 0 30px 10px rgba(255, 150, 100, 0.7),
                            0 0 45px 15px rgba(255, 100, 50, 0.7);
              }
            }
          `}
        </style>
      </motion.section>

      {/* 🌺 Intro Section */}
      <motion.section
        className="text-center py-5"
        style={{
          background: "linear-gradient(to bottom, #fffaf3, #fdf2e9)",
        }}
        initial="hidden"
        whileInView="visible"
        variants={fadeUp}
        viewport={{ once: true }}
      >
        <div className="container">
          <h2 className="fw-bold mb-3" style={{ color: "#573a1f" }}>
            What is Bhaktamar?
          </h2>
          <p
            style={{
              fontSize: "1.1rem",
              color: "#6b4a2e",
              maxWidth: "800px",
              margin: "auto",
              lineHeight: "1.8",
            }}
          >
            The Bhaktamar Stotra is an ancient Jain composition of 48 verses,
            celebrated for its divine energy and spiritual healing powers. Each
            verse resonates with vibrations of devotion, leading seekers on a
            path of peace, clarity, and enlightenment.
          </p>
        </div>
      </motion.section>

      {/* 💠 Explore Cards */}
      {/* <motion.section
        className="py-5"
        style={{
          background: "linear-gradient(120deg, #5e60ce, #64dfdf, #80ffdb)",
        }}
        initial="hidden"
        whileInView="visible"
        variants={fadeUp}
        viewport={{ once: true }}
      >
        <div className="container text-center">
          <h2 className="fw-bold text-white mb-5">Explore More</h2>
          <div className="row g-4">
            {[
              { title: "Shlokas", desc: "Explore all 48 Shlokas", link: "/verses" },
              { title: "Healing", desc: "Discover healing powers", link: "/healing" },
              { title: "Resources", desc: "Guides & Audios", link: "/resources" },
              { title: "About", desc: "Know Acharya Ji", link: "/about" },
            ].map((item, i) => (
              <motion.div
                className="col-md-3 col-sm-6"
                key={i}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: i * 0.2 }}
                viewport={{ once: true }}
              >
                <Link
                  to={item.link}
                  style={{ textDecoration: "none", color: "#fff" }}
                >
                  <div
                    className="card h-100 shadow-lg"
                    style={{
                      background: "rgba(255,255,255,0.15)",
                      border: "none",
                      borderRadius: "20px",
                      backdropFilter: "blur(8px)",
                      transition: "all 0.4s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-10px)";
                      e.currentTarget.style.background =
                        "rgba(255,255,255,0.25)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.background =
                        "rgba(255,255,255,0.15)";
                    }}
                  >
                    <div className="card-body d-flex flex-column justify-content-center align-items-center">
                      <h5 className="fw-bold mb-2">{item.title}</h5>
                      <p style={{ fontSize: "0.95rem" }}>{item.desc}</p>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section> */}

      {/* 💠 Explore Cards */}
      <motion.section
        className="py-5"
        style={{
          background: "linear-gradient(120deg, #fff4e2, #ffe7c2, #fffaf3)",
        }}
        initial="hidden"
        whileInView="visible"
        variants={fadeUp}
        viewport={{ once: true }}
      >
        <div className="container text-center">
          <h2
            className="fw-bold mb-5"
            style={{
              color: "#b66b00",
              textShadow: "0 2px 10px rgba(255,200,120,0.4)",
            }}
          >
            Explore More
          </h2>

          <div className="row g-4">
            {[
              { title: "Shlokas", desc: "Explore all 48 Shlokas", link: "/verses" },
              { title: "Healing", desc: "Discover healing powers", link: "/healing" },
              { title: "Resources", desc: "Guides & Audios", link: "/resources" },
              { title: "About", desc: "Know Acharya Ji and Bhaktamar", link: "/about" },
            ].map((item, i) => (
              <motion.div
                className="col-md-3 col-sm-6"
                key={i}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: i * 0.2 }}
                viewport={{ once: true }}
              >
                <Link
                  to={item.link}
                  style={{ textDecoration: "none", color: "#5a3a0e" }}
                >
                  <div
                    className="card h-100 shadow-lg"
                    style={{
                      background: "linear-gradient(145deg, #fffaf3, #fff3e1)",
                      border: "1px solid rgba(255,200,150,0.4)",
                      borderRadius: "20px",
                      transition: "all 0.4s ease",
                      boxShadow: "0 6px 20px rgba(0,0,0,0.05)",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-10px)";
                      e.currentTarget.style.boxShadow =
                        "0 10px 25px rgba(255,200,120,0.5)";
                      e.currentTarget.style.background =
                        "linear-gradient(145deg, #fff2da, #ffebc2)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow =
                        "0 6px 20px rgba(0,0,0,0.05)";
                      e.currentTarget.style.background =
                        "linear-gradient(145deg, #fffaf3, #fff3e1)";
                    }}
                  >
                    <div className="card-body d-flex flex-column justify-content-center align-items-center p-4">
                      <h5
                        className="fw-bold mb-2"
                        style={{
                          color: "#b66b00",
                          fontSize: "1.25rem",
                          letterSpacing: "0.5px",
                        }}
                      >
                        {item.title}
                      </h5>
                      <p
                        style={{
                          fontSize: "1rem",
                          color: "#5a3a0e",
                          opacity: 0.9,
                        }}
                      >
                        {item.desc}
                      </p>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>

        {/* 📱 Responsive Styles */}
        <style>
          {`
      @media (max-width: 992px) {
        h2 { font-size: 2rem !important; }
        h5 { font-size: 1.1rem !important; }
        p { font-size: 0.95rem !important; }
      }

      @media (max-width: 576px) {
        h2 { font-size: 1.6rem !important; }
        h5 { font-size: 1rem !important; }
        p { font-size: 0.9rem !important; }
        .card { border-radius: 15px !important; }
      }
    `}
        </style>
      </motion.section>


      {/* 🌿 Closing Quote */}
      <motion.section
        className="text-center py-5"
        style={{
          backgroundColor: "#fff",
        }}
        initial="hidden"
        whileInView="visible"
        variants={fadeUp}
        viewport={{ once: true }}
      >
        <div className="container">
          <motion.h2
            className="fw-bold mb-4"
            style={{
              color: "#4b2e05",
              textShadow: "0 2px 8px rgba(255, 150, 50, 0.4)",
            }}
          >
            "Discover the Power Within"
          </motion.h2>
          <p
            style={{
              fontSize: "1.2rem",
              color: "#555",
              maxWidth: "700px",
              margin: "auto",
              lineHeight: "1.8",
            }}
          >
            Unlock your inner potential through the wisdom of Bhaktamar Stotra
            and find your true spiritual path.
          </p>
        </div>
      </motion.section>
    </motion.div>
  );
}

export default Home;
